import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getNoticeData, formatDate, convertDriveUrl } from '../services/googleSheets';
import './NoticeList.css';
import ShareButton from './ShareButton';

const NoticeList = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const data = await getNoticeData();
        // 최신순으로 정렬 (ID 역순)
        const sortedData = data.sort((a, b) => b.id - a.id);
        setNotices(sortedData);
      } catch (error) {
        console.error('공지사항을 불러오는데 실패했습니다:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNotices();
  }, []);

  // 페이지네이션 계산
  // 첨부파일이 있는 공지사항만 필터링
  const filteredNotices = notices.filter(notice => notice['첨부파일URL'] && notice['첨부파일URL'].trim());
  const totalPages = Math.ceil(filteredNotices.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentNotices = filteredNotices.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 첨부파일 이름을 표시하는 컴포넌트
  const AttachmentDisplay = ({ url, fileName }) => {
    const downloadUrl = convertDriveUrl(url) || url;
    const displayName = fileName || '첨부파일';
    
    return (
      <a 
        href={downloadUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="notice-attachment-inline"
        title={displayName}
      >
        <span className="attachment-icon">📎</span>
        <span className="attachment-name">{displayName}</span>
      </a>
    );
  };

  if (loading) {
    return (
      <div className="notice-loading">
        <div className="loading-spinner"></div>
        <p>공지사항을 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div className="notice-list-container">
      <div className="notice-header">
        <h1>📢 공지사항</h1>
        <p>청소년 스마트폰 프리 운동본부의 최신 소식을 확인하세요</p>
      </div>

      <div className="notice-stats">
        <span>전체 {filteredNotices.length}건</span>
        <span>페이지 {currentPage} / {totalPages}</span>
      </div>

      <div className="notice-list">
        {currentNotices.length > 0 ? (
          <>
            {/* 데스크톱 테이블 뷰 */}
            <table className="notice-table">
              <thead>
                <tr>
                  <th className="notice-number">번호</th>
                  <th className="notice-title-header">제목</th>
                  <th className="notice-attachment-header">첨부</th>
                  <th className="notice-date-header">작성일</th>
                </tr>
              </thead>
              <tbody>
                {currentNotices.map((notice) => (
                  <tr key={notice.id} className="notice-row">
                    <td className="notice-number">{notice.id}</td>
                    <td className="notice-title-cell">
                      <Link to={`/notice/${notice.id}`} className="notice-link">
                        {notice['제목'] || '제목 없음'}
                      </Link>
                    </td>
                    <td className="notice-attachment-cell">
                      {notice['첨부파일URL'] && (
                        <AttachmentDisplay 
                          url={notice['첨부파일URL']} 
                          fileName={notice['첨부파일이름']}
                        />
                      )}
                    </td>
                    <td className="notice-date-cell">{formatDate(notice['작성일'])}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* 모바일 카드 뷰 */}
            <div className="notice-cards">
              {currentNotices.map((notice) => (
                <div key={notice.id} className="notice-card">
                  <div className="notice-card-header">
                    <div className="notice-card-info">
                      <span className="notice-card-number">
                        📢 {notice.id}
                      </span>
                      <span className="notice-card-date">
                        📅 {formatDate(notice['작성일'])}
                      </span>
                      <span className="notice-card-category">
                        📋 공지사항
                      </span>
                    </div>
                  </div>
                  
                  <Link 
                    to={`/notice/${notice.id}`} 
                    className="notice-card-title"
                    style={{ textDecoration: 'none' }}
                  >
                    {notice['제목'] || '제목 없음'}
                  </Link>
                  
                  {notice['내용'] && (
                    <div className="notice-card-content">
                      {notice['내용'].substring(0, 100)}...
                    </div>
                  )}
                  
                  <div className="notice-card-footer">
                    <div></div>
                    {notice['첨부파일URL'] && (
                      <AttachmentDisplay 
                        url={notice['첨부파일URL']} 
                        fileName={notice['첨부파일이름']}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="notice-empty">
            <p>등록된 공지사항이 없습니다.</p>
          </div>
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-btn"
          >
            ‹ 이전
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
            >
              {page}
            </button>
          ))}
          
          <button 
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-btn"
          >
            다음 ›
          </button>
        </div>
      )}
    </div>
  );
};

export default NoticeList; 